import { Box } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import type { GridApi, GridColDef, GridRowClassNameParams } from '@mui/x-data-grid-premium';
import React from 'react';
import { useLastLineOfEntryIds } from '../hooks/use-last-line-of-entry-ids';
import type { JournalEntryRow } from '../utils/flatten-journal-entries';
import { formatJournalEntryAccount } from '../utils/format-journal-entry-account';

const COLUMNS: GridColDef<JournalEntryRow>[] = [
  { field: 'customerName', headerName: 'Customer', width: 220 },
  { field: 'periodDate', headerName: 'Date', width: 120 },
  { field: 'description', headerName: 'JE Description', flex: 1, minWidth: 260 },
  {
    field: 'account',
    headerName: 'Account',
    width: 180,
    valueGetter: (_value, row) => formatJournalEntryAccount(row.account),
  },
  { field: 'debit', headerName: 'Debit', type: 'number', width: 140 },
  { field: 'credit', headerName: 'Credit', type: 'number', width: 140 },
];

const INITIAL_STATE = {
  pagination: { paginationModel: { pageSize: 50 } },
};

type Props = {
  rows: JournalEntryRow[];
  apiRef?: React.MutableRefObject<GridApi>;
};

export const JournalEntriesReportTable: React.FC<Props> = ({ rows, apiRef }) => {
  const internalApiRef = React.useRef<GridApi>(null as unknown as GridApi);
  const effectiveApiRef = apiRef ?? internalApiRef;

  const lastLineIds = useLastLineOfEntryIds(effectiveApiRef, rows);

  const getRowClassName = React.useCallback(
    (params: GridRowClassNameParams<JournalEntryRow>) =>
      lastLineIds.has(params.id) ? 'je-last-line' : '',
    [lastLineIds],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        '& .je-last-line': {
          '& .MuiDataGrid-cell': {
            borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
          },
        },
      }}
    >
      <DataGridPremium
        apiRef={effectiveApiRef}
        rows={rows}
        columns={COLUMNS}
        density="compact"
        pagination
        initialState={INITIAL_STATE}
        getRowClassName={getRowClassName}
      />
    </Box>
  );
};
